import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Compass, Loader2, SlidersHorizontal, LayoutList, ArrowDownNarrowWide, ChevronDown, ChevronUp, Check, Eye, Droplets, Landmark, Star, Castle, Skull, Info, Mountain } from 'lucide-react';
import type { DayForecast, DiscoverPOI, Coordinates, AppUser } from '../../types';
import { getDistance } from '../../services/geometryUtils';
import WeatherBar from './WeatherBar';
import DiscoverCard from './DiscoverCard';
import AuthNudge from '../common/AuthNudge';

// All filterable categories
const FILTER_CATEGORIES = [
  { key: 'weather', labelKey: 'discover.weather_title' },
  { key: 'viewpoint', labelKey: 'discover.viewpoint' },
  { key: 'waterfall', labelKey: 'discover.waterfall' },
  { key: 'museum', labelKey: 'discover.museum' },
  { key: 'monument', labelKey: 'discover.monument' },
  { key: 'memorial', labelKey: 'discover.memorial' },
  { key: 'castle', labelKey: 'discover.castle' },
  { key: 'ruins', labelKey: 'discover.ruins' },
  { key: 'attraction', labelKey: 'discover.attraction' },
  { key: 'peak', labelKey: 'discover.peak' },
  { key: 'information', labelKey: 'discover.information' },
  { key: 'cultural_heritage', labelKey: 'discover.cultural_heritage' },
] as const;

const CATEGORY_ICONS: Record<string, React.ComponentType<{ size?: number; className?: string }>> = {
  viewpoint: Eye,
  waterfall: Droplets,
  museum: Landmark,
  monument: Star,
  memorial: Star,
  castle: Castle,
  ruins: Skull,
  attraction: Star,
  information: Info,
  peak: Mountain,
  cultural_heritage: Landmark,
};

const CATEGORY_ICON_COLORS: Record<string, string> = {
  viewpoint: 'text-emerald-400',
  waterfall: 'text-cyan-400',
  museum: 'text-amber-400',
  monument: 'text-rose-400',
  memorial: 'text-rose-400',
  castle: 'text-amber-400',
  ruins: 'text-stone-400',
  attraction: 'text-rose-400',
  information: 'text-blue-400',
  peak: 'text-emerald-400',
  cultural_heritage: 'text-amber-500',
};

const LS_HIDDEN_KEY = 'discover_hidden_cats';
const LS_VIEW_KEY = 'discover_view_mode';

function loadHidden(): Set<string> {
  try { return new Set(JSON.parse(localStorage.getItem(LS_HIDDEN_KEY) || '[]')); }
  catch { return new Set(); }
}

function saveHidden(s: Set<string>) {
  try { localStorage.setItem(LS_HIDDEN_KEY, JSON.stringify([...s])); } catch {}
}

function loadViewMode(): 'grouped' | 'distance' {
  const v = localStorage.getItem(LS_VIEW_KEY);
  return v === 'distance' ? 'distance' : 'grouped';
}

interface DiscoverPanelProps {
  weather: DayForecast[];
  attractions: DiscoverPOI[];
  heritage: DiscoverPOI[];
  loading: boolean;
  error: boolean;
  center: Coordinates;
  selectedPoiId?: string | null;
  onSelectItem: (id: string, lat: number, lon: number) => void;
  onFilterChange?: (hiddenCategories: Set<string>) => void;
  user: AppUser | null;
  onOpenAuth: () => void;
}

const DiscoverPanel: React.FC<DiscoverPanelProps> = ({
  weather,
  attractions,
  heritage,
  loading,
  error,
  center,
  selectedPoiId = null,
  onSelectItem,
  onFilterChange,
  user,
  onOpenAuth,
}) => {
  const { t } = useTranslation();

  const [hiddenCategories, setHiddenCategories] = useState<Set<string>>(loadHidden);
  const [viewMode, setViewMode] = useState<'grouped' | 'distance'>(loadViewMode);
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());

  const toggleCategory = useCallback((key: string) => {
    setHiddenCategories(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      saveHidden(next);
      onFilterChange?.(next);
      return next;
    });
  }, [onFilterChange]);

  const showAll = useCallback(() => {
    const empty = new Set<string>();
    setHiddenCategories(empty);
    saveHidden(empty);
    onFilterChange?.(empty);
  }, [onFilterChange]);

  const hideAll = useCallback(() => {
    const all = new Set(FILTER_CATEGORIES.map(c => c.key));
    setHiddenCategories(all);
    saveHidden(all);
    onFilterChange?.(all);
  }, [onFilterChange]);

  const handleViewMode = useCallback((mode: 'grouped' | 'distance') => {
    setViewMode(mode);
    try { localStorage.setItem(LS_VIEW_KEY, mode); } catch {}
  }, []);

  const toggleSection = useCallback((section: string) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      if (next.has(section)) next.delete(section); else next.add(section);
      return next;
    });
  }, []);

  // Auto-expand section containing selected POI (e.g. when clicking map marker)
  useEffect(() => {
    if (!selectedPoiId || viewMode !== 'grouped') return;
    const poi = [...attractions, ...heritage].find(p => p.id === selectedPoiId);
    if (poi && !expandedSections.has(poi.category)) {
      setExpandedSections(prev => new Set(prev).add(poi.category));
    }
  }, [selectedPoiId, viewMode, attractions, heritage]);

  // All POIs combined, filtered by hidden categories
  const allFiltered = useMemo(
    () => [...attractions, ...heritage].filter(p => !hiddenCategories.has(p.category)),
    [attractions, heritage, hiddenCategories],
  );
  const showWeather = !hiddenCategories.has('weather');

  // Per-category groups for grouped mode (preserves FILTER_CATEGORIES order)
  const categoryGroups = useMemo(() => {
    if (viewMode !== 'grouped') return [];
    const map = new Map<string, DiscoverPOI[]>();
    for (const poi of allFiltered) {
      const list = map.get(poi.category);
      if (list) list.push(poi); else map.set(poi.category, [poi]);
    }
    // Return in FILTER_CATEGORIES order (skip 'weather')
    return FILTER_CATEGORIES
      .filter(c => c.key !== 'weather' && map.has(c.key))
      .map(c => ({ key: c.key, labelKey: c.labelKey, items: map.get(c.key)! }));
  }, [viewMode, allFiltered]);

  // Combined & distance-sorted list for distance mode
  const distanceSorted = useMemo(() => {
    if (viewMode !== 'distance') return [];
    return [...allFiltered].sort((a, b) => {
      const da = getDistance(center.latitude, center.longitude, a.lat, a.lon);
      const db = getDistance(center.latitude, center.longitude, b.lat, b.lon);
      return da - db;
    });
  }, [viewMode, allFiltered, center]);

  const isEmpty = weather.length === 0 && attractions.length === 0 && heritage.length === 0;
  const hiddenCount = hiddenCategories.size;

  if (loading && isEmpty) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-3 text-[var(--text-muted)]">
        <Loader2 size={28} className="animate-spin text-amber-400" />
        <span className="text-xs">{t('discover.loading')}</span>
      </div>
    );
  }

  if (!loading && isEmpty) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-3 text-center px-8">
        <div className="w-14 h-14 rounded-2xl bg-amber-500/15 flex items-center justify-center">
          <Compass size={28} className="text-amber-400" />
        </div>
        <p className="text-sm font-semibold text-[var(--text-primary)]">{t('discover.empty')}</p>
        <p className="text-xs text-[var(--text-muted)]">{t('discover.empty_sub')}</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1 pb-8">
      {/* Toolbar — sticky so it stays visible while scrolling */}
      <div className="sticky top-0 z-10 flex items-center justify-between px-4 pt-3 pb-1 bg-[var(--bg-surface-alt)]">
        <div className="flex items-center gap-1.5">
          {/* Filter button */}
          <button
            onClick={() => setShowFilterPanel(p => !p)}
            className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-2xs font-bold transition-all ${
              showFilterPanel
                ? 'bg-amber-500/20 text-amber-300 ring-1 ring-amber-500/30'
                : 'bg-[var(--bg-surface-alt)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
            }`}
          >
            <SlidersHorizontal size={12} />
            {t('discover.filter')}
            {hiddenCount > 0 && (
              <span className="ml-0.5 px-1 py-px rounded bg-amber-500/30 text-amber-300 text-4xs font-black">
                {t('discover.hidden_count', { count: hiddenCount })}
              </span>
            )}
          </button>
        </div>

        {/* View mode toggle */}
        <div className="flex items-center bg-[var(--bg-surface-alt)] rounded-lg p-0.5">
          <button
            onClick={() => handleViewMode('grouped')}
            className={`flex items-center gap-1 px-2 py-1 rounded-md text-2xs font-bold transition-all ${
              viewMode === 'grouped'
                ? 'bg-amber-500/20 text-amber-300'
                : 'text-[var(--text-muted)] hover:text-[var(--text-secondary)]'
            }`}
            title={t('discover.view_grouped')}
          >
            <LayoutList size={11} />
            <span className="hidden sm:inline">{t('discover.view_grouped')}</span>
          </button>
          <button
            onClick={() => handleViewMode('distance')}
            className={`flex items-center gap-1 px-2 py-1 rounded-md text-2xs font-bold transition-all ${
              viewMode === 'distance'
                ? 'bg-amber-500/20 text-amber-300'
                : 'text-[var(--text-muted)] hover:text-[var(--text-secondary)]'
            }`}
            title={t('discover.view_distance')}
          >
            <ArrowDownNarrowWide size={11} />
            <span className="hidden sm:inline">{t('discover.view_distance')}</span>
          </button>
        </div>
      </div>

      {/* Filter panel (collapsible, sticky below toolbar) */}
      {showFilterPanel && (
        <div className="sticky top-[40px] z-10 mx-4 mb-2 p-3 rounded-xl bg-[var(--bg-surface-alt)] border border-[var(--border-primary)] animate-in slide-in-from-top-2">
          <div className="flex items-center justify-between mb-2">
            <p className="text-3xs font-black text-[var(--text-muted)] uppercase tracking-widest">
              {t('discover.categories')}
            </p>
            <div className="flex gap-2">
              <button onClick={showAll} className="text-3xs font-bold text-amber-400 hover:text-amber-300">
                {t('discover.show_all')}
              </button>
              <button onClick={hideAll} className="text-3xs font-bold text-[var(--text-muted)] hover:text-[var(--text-secondary)]">
                {t('discover.hide_all')}
              </button>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-1">
            {FILTER_CATEGORIES.map(cat => {
              const isVisible = !hiddenCategories.has(cat.key);
              return (
                <button
                  key={cat.key}
                  onClick={() => toggleCategory(cat.key)}
                  className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-2xs font-semibold transition-all ${
                    isVisible
                      ? 'bg-amber-500/15 text-amber-300'
                      : 'bg-transparent text-[var(--text-muted)] opacity-60'
                  }`}
                >
                  <div className={`w-3.5 h-3.5 rounded border flex items-center justify-center flex-shrink-0 ${
                    isVisible
                      ? 'bg-amber-500 border-amber-500'
                      : 'border-[var(--border-secondary)]'
                  }`}>
                    {isVisible && <Check size={9} className="text-white" strokeWidth={3} />}
                  </div>
                  {t(cat.labelKey)}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Weather bar */}
      {showWeather && (
        <div className="px-4">
          <WeatherBar forecasts={weather} />
        </div>
      )}

      {/* Distance mode — flat sorted list */}
      {viewMode === 'distance' && distanceSorted.length > 0 && (
        <div className="px-4">
          {!user ? (
            <div className="bg-[var(--bg-surface-alt)] rounded-2xl border border-[var(--border-primary)] overflow-hidden">
              <AuthNudge
                title={t('auth.discover_title')}
                description={t('auth.discover_desc')}
                onOpenAuth={onOpenAuth}
              />
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              {distanceSorted.map(poi => (
                <DiscoverCard key={poi.id} poi={poi} center={center} isSelected={selectedPoiId === poi.id} onSelect={onSelectItem} />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Grouped mode — per-category collapsible sections */}
      {viewMode === 'grouped' && categoryGroups.map((group, i) => (
        <div key={group.key} className={`px-4${i > 0 ? ' mt-3' : ''}`}>
          <button
            onClick={() => toggleSection(group.key)}
            className="w-full flex items-center justify-between mb-2 px-1 group"
          >
            <p className="text-3xs font-black text-[var(--text-muted)] uppercase tracking-widest flex items-center gap-1.5">
              {(() => { const Icon = CATEGORY_ICONS[group.key]; return Icon ? <Icon size={11} className={CATEGORY_ICON_COLORS[group.key] || ''} /> : null; })()}
              {t(group.labelKey)} ({group.items.length})
              {loading && <Loader2 size={9} className="animate-spin text-amber-400" />}
            </p>
            {!expandedSections.has(group.key)
              ? <ChevronDown size={12} className="text-[var(--text-muted)] group-hover:text-[var(--text-secondary)]" />
              : <ChevronUp size={12} className="text-[var(--text-muted)] group-hover:text-[var(--text-secondary)]" />}
          </button>
          {expandedSections.has(group.key) && (
            user ? (
              <div className="flex flex-col gap-2">
                {group.items.map(poi => (
                  <DiscoverCard key={poi.id} poi={poi} center={center} isSelected={selectedPoiId === poi.id} onSelect={onSelectItem} />
                ))}
              </div>
            ) : (
              <div className="bg-[var(--bg-surface-alt)] rounded-2xl border border-[var(--border-primary)] overflow-hidden">
                <AuthNudge
                  title={t('auth.discover_title')}
                  description={t('auth.discover_desc')}
                  onOpenAuth={onOpenAuth}
                />
              </div>
            )
          )}
        </div>
      ))}

      {/* Empty after filtering */}
      {allFiltered.length === 0 && !loading && (
        <div className="flex flex-col items-center py-8 gap-2 text-center px-8">
          <SlidersHorizontal size={20} className="text-[var(--text-muted)]" />
          <p className="text-xs text-[var(--text-muted)]">
            {hiddenCount > 0 ? t('discover.hidden_count', { count: hiddenCount }) : t('discover.empty')}
          </p>
        </div>
      )}

      {/* Loading indicator for refetch */}
      {loading && !isEmpty && (
        <div className="flex justify-center py-3">
          <Loader2 size={16} className="animate-spin text-amber-400" />
        </div>
      )}

      {/* Attribution */}
      <div className="px-5 pt-4 flex flex-col gap-0.5">
        {showWeather && weather.length > 0 && (
          <p className="text-4xs text-[var(--text-muted)]">{t('discover.attribution_met')}</p>
        )}
        {allFiltered.some(p => p.type === 'heritage') && (
          <p className="text-4xs text-[var(--text-muted)]">{t('discover.attribution_ra')}</p>
        )}
      </div>
    </div>
  );
};

export default DiscoverPanel;
